import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';

import { GOAL_SERVICES, serviceHasActivities, goalDomainSuggestions } from 'utils/goalServices';
import { activityLines } from 'utils/activityList';

// Strip the canonical "DOMAIN-" prefix to get the bare Skill-Repository code
// ("DOMAIN-MAND" -> "MAND"). Live iep_goal rows store the bare code; the form
// and the domain dropdown use the "DOMAIN-CODE" id form.
const bareCode = (v) =>
  String(v || '')
    .replace(/^DOMAIN-/i, '')
    .trim();

/**
 * Shared free-form (custom) goal editor used for BOTH adding and editing a goal
 * in the IEP tab and the child's Goals & Targets tab — the counterpart to the
 * repository-driven GoalBuilderModal. Standardizes the "author a goal"
 * experience across the two tabs (same modal, same fields).
 *
 * Deliberately i18n-light (plain English labels), matching its sibling
 * GoalBuilderModal — the goal vocabulary ("Goal", "Skill domain", "baseline")
 * stays in English throughout the product.
 *
 * Contract:
 *   - `initial` null  → ADD mode (goal code taken from `nextCode`).
 *   - `initial` object → EDIT mode (its `goalId` is preserved read-only).
 *   - Start/End dates default to BLANK on add — the author fills them in later.
 *   - onSave receives a normalized object and does the persistence:
 *       { goalId, name, goalDescription, serviceCode, activities, domainId,
 *         domainCode, status, startDate, endDate, baseline }
 *     onSave may be async; the buttons stay disabled until it resolves so a
 *     double-click can't create the goal twice.
 *
 * `name` is the goal STATEMENT (stored as iep_goal.description, which is where
 * every existing reader looks for it) and `goalDescription` the narrative the
 * author writes under it (iep_goal.goal_description). `activities` is the
 * ordered list of session activities, only collected for the therapy services
 * (speech / OT); an ABA goal is worked through its targets instead, so it is
 * always sent as [].
 */
const GoalDetailsModal = ({
  open,
  onClose,
  onSave,
  initial = null,
  statusOptions = [],
  nextCode = '',
  unassignedId = '__unassigned__'
}) => {
  const isEdit = Boolean(initial);

  const [name, setName] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [serviceCode, setServiceCode] = useState('');
  // Activity rows. Held as { id, text } rather than bare strings so a row keeps
  // its React key while it is being typed into — keying by array index makes
  // deleting a middle row re-use the deleted row's key and carry its DOM state
  // (and its focus) onto the row that took its place. Flattened to plain
  // strings on save, which is the shape the data sheets store.
  const [activities, setActivities] = useState([]);
  const [domainId, setDomainId] = useState('');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [baseline, setBaseline] = useState('');

  // In-flight guard — onSave is async (create/update + snapshot mirror, several
  // round-trips) and keeps the modal open until it finishes, so without this a
  // double-click fires onSave twice and creates a duplicate goal.
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || initial?.description || '');
    setGoalDescription(initial?.goalDescription || '');
    setServiceCode(initial?.serviceCode || '');
    setActivities(activityLines(initial?.activities).map((text, i) => ({ id: `a-${i}`, text })));
    // Skill domain is a free-text field (no repository dropdown). Show the bare
    // stored value — a code like 'MAND' from a legacy repo-tagged goal, or the
    // free text a custom goal was given — so an untouched edit saves it back
    // unchanged. The '__unassigned__' sentinel is treated as empty.
    const raw = initial?.domain || '';
    setDomainId(!raw || bareCode(raw) === unassignedId ? '' : bareCode(raw));
    setStatus(initial?.status || statusOptions[0]?.value || '');
    setStartDate(initial?.startDate || '');
    setEndDate(initial?.endDate || '');
    setBaseline(initial?.baseline || '');
    submittingRef.current = false;
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const goalId = isEdit ? initial?.goalId : nextCode;

  // Activities only apply to the therapy services. Switching a goal to ABA hides
  // the list but does NOT clear it — a mis-click on the service dropdown would
  // otherwise silently destroy a list the author had typed out. It is dropped on
  // save instead, where the choice is deliberate.
  const showActivities = serviceHasActivities(serviceCode);

  // Skill-domain suggestions follow the chosen service (ABA / Speech / OT). The
  // field stays free-text, so these only seed the dropdown — a domain not on the
  // list can still be typed. Changing the service swaps the suggestions but never
  // clears an already-entered domain (same reasoning as the activities list).
  const domainSuggestions = goalDomainSuggestions(serviceCode);

  const addActivityRow = () => setActivities((prev) => [...prev, { id: `a-${Date.now()}-${prev.length}`, text: '' }]);
  const setActivityText = (id, text) => setActivities((prev) => prev.map((a) => (a.id === id ? { ...a, text } : a)));
  const removeActivityRow = (id) => setActivities((prev) => prev.filter((a) => a.id !== id));

  const commit = async () => {
    if (submittingRef.current || !name.trim()) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onSave({
        goalId,
        name: name.trim(),
        goalDescription: goalDescription.trim(),
        serviceCode,
        // Blank rows are the residue of an "+ Add row" the author never filled
        // in; they are not activities and must not be persisted as empty ones.
        activities: showActivities ? activities.map((a) => a.text.trim()).filter(Boolean) : [],
        // Free text now: domainId and domainCode are the same trimmed value (the
        // 'DOMAIN-' prefix is stripped in case one is typed by hand). Downstream
        // stores this as skill_domain_code and groups goals by it.
        domainId: bareCode(domainId),
        domainCode: bareCode(domainId),
        status,
        startDate,
        endDate,
        baseline: baseline.trim()
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  // A goal whose stored status is not in the current option list still has to
  // SHOW its value (an option list can lag the data) — inject a fallback so the
  // control renders the value instead of going blank.
  const statusNeedsFallback = status && !statusOptions.some((s) => s.value === status);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEdit ? 'Edit goal' : 'Add goal'}
        <IconButton size="small" onClick={onClose} disabled={submitting}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            label="Goal"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
            placeholder="Describe the goal in the child's own terms."
          />
          <TextField
            select
            label="Service"
            fullWidth
            size="small"
            value={serviceCode}
            onChange={(e) => setServiceCode(e.target.value)}
            helperText="Speech and Occupational Therapy goals carry a list of session activities."
          >
            <MenuItem value="">
              <em>Not set</em>
            </MenuItem>
            {GOAL_SERVICES.map((sv) => (
              <MenuItem key={sv.code} value={sv.code}>
                {sv.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Goal description"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={goalDescription}
            onChange={(e) => setGoalDescription(e.target.value)}
            placeholder="What this goal means in practice — scope, context, how it is worked."
          />
          <Grid container spacing={1.5}>
            {/* Status is auto-managed for live goals (the Goals & Targets tab
                auto-completes a goal when its targets master), so that caller
                passes an empty statusOptions to hide this field; the IEP form
                passes its snapshot-status options and shows it. */}
            <Grid size={{ xs: 12, sm: statusOptions.length > 0 ? 6 : 12 }}>
              {/* Service-driven suggestions in a creatable dropdown — the same
                  pattern as the SLP/OT data sheets: pick a listed domain, or type
                  one that isn't there and select the "+ Add …" row to add it.
                  Not tied to the Skills Repository. Capped at 50 chars to match
                  the backend skill_domain_code column. */}
              <Autocomplete
                freeSolo
                // Commit whatever is typed when focus leaves (e.g. the author goes
                // straight to "Add goal"), so a custom domain isn't lost by not
                // pressing Enter or clicking the "+ Add" row first.
                autoSelect
                size="small"
                fullWidth
                options={domainSuggestions}
                value={domainId || null}
                selectOnFocus
                handleHomeEndKeys
                isOptionEqualToValue={(o, v) => (typeof o === 'string' ? o === v : false)}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
                filterOptions={(opts, params) => {
                  const q = params.inputValue.trim().toLowerCase();
                  const filtered = q ? opts.filter((o) => o.toLowerCase().includes(q)) : opts.slice();
                  const typed = params.inputValue.trim();
                  if (typed && !opts.some((o) => o.toLowerCase() === typed.toLowerCase())) {
                    filtered.push({ create: typed, label: `+ Add "${typed}"` });
                  }
                  return filtered;
                }}
                onChange={(_e, val) => {
                  if (val == null) return setDomainId('');
                  if (typeof val === 'string') return setDomainId(val.trim());
                  if (val.create != null) return setDomainId(val.create);
                  return setDomainId('');
                }}
                renderOption={(props, o) => {
                  const isCreate = typeof o !== 'string';
                  return (
                    <li {...props} key={isCreate ? o.label : o} style={isCreate ? { color: '#1a73e8', fontWeight: 500 } : undefined}>
                      {isCreate ? o.label : o}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Skill domain"
                    placeholder="Select a domain or type your own"
                    inputProps={{ ...params.inputProps, maxLength: 50 }}
                  />
                )}
              />
            </Grid>
            {statusOptions.length > 0 && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {statusNeedsFallback && <MenuItem value={status}>{String(status).replace(/_/g, ' ')}</MenuItem>}
                  {statusOptions.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                type="date"
                fullWidth
                size="small"
                label="Start date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                type="date"
                fullWidth
                size="small"
                label="End date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Grid>
          </Grid>
          <TextField
            label="Current baseline"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={baseline}
            onChange={(e) => setBaseline(e.target.value)}
            placeholder="Describe the child's current performance for this goal."
          />
          {showActivities && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Activities
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {activities.length === 0 ? 'None yet' : `${activities.length} row${activities.length === 1 ? '' : 's'}`}
                </Typography>
              </Stack>
              {activities.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ mb: 1 }}>
                  No activities added yet.
                </Typography>
              ) : (
                <Stack spacing={1} sx={{ mb: 1 }}>
                  {activities.map((a, i) => (
                    <Stack key={a.id} direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ width: 20, flexShrink: 0, textAlign: 'right' }}>
                        {i + 1}.
                      </Typography>
                      <TextField
                        size="small"
                        fullWidth
                        value={a.text}
                        onChange={(e) => setActivityText(a.id, e.target.value)}
                        placeholder="Describe the activity"
                        // Enter adds the next row, so a list can be typed out
                        // without reaching for the mouse between entries.
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addActivityRow();
                          }
                        }}
                      />
                      <IconButton size="small" color="error" onClick={() => removeActivityRow(a.id)} aria-label="Remove activity">
                        <DeleteOutlined style={{ fontSize: 14 }} />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}
              {/* Same dashed "+ Add Row" affordance as the OT / SLP data sheets,
                  so building an activity list looks the same wherever it happens. */}
              <Button
                onClick={addActivityRow}
                fullWidth
                sx={{
                  py: 1.25,
                  border: '1px dashed',
                  borderColor: 'primary.light',
                  borderRadius: 1,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
                  fontWeight: 500,
                  '&:hover': { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }
                }}
              >
                + Add Row
              </Button>
            </Box>
          )}
          {goalId && (
            <Typography variant="caption" color="text.secondary">
              Goal ID: {goalId}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={commit}
          disabled={!name.trim() || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Saving…' : isEdit ? 'Save goal' : 'Add goal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GoalDetailsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initial: PropTypes.object,
  statusOptions: PropTypes.array,
  nextCode: PropTypes.string,
  unassignedId: PropTypes.string
};

export default GoalDetailsModal;
